import React, { useEffect, useRef, useState } from "react";
import { Space, Table, Tag, Button, Input, message, Modal, Checkbox, Spin, Col, Drawer, Form, Row } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import Column from "antd/es/table/Column";
import { compareObjects, isNotNullUndefined } from "../helpers/common";
import { useAddLanguageInterface, useDeleteLanguageInterface, useUpdateLanguageInterface, useFetchLanguageInterface } from "../services/languageinterface.service";
import { ILanguageInterface } from "../types/languageInterfaces"
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { eventEmitter } from "../helpers/eventemitter";
import { useAuth } from "../context/AuthContext";

const Languageinterface: React.FC = () => {
    const { Search } = Input;
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [languageinterfaces, setLanguageinterfaces] = useState<any[]>([]);
    const isAddLanguageInterface = useRef(false);
    const [editingLanguageInterface, setEditingLanguageInterface] = useState<ILanguageInterface | null>(null);
    const [modalType, setModalType] = useState<'delete' | 'update' | 'add' | ''>('');
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [languageinterfaceId, setLanguageinterfaceId] = useState();
    const [formUpdateValues, setFormUpdateValues] = useState<any>({});
    const [isLoading, setIsLoading] = useState(false);
    const formInitialValues = useRef<any>({});
    const formUpdatedValues = useRef<any>({});
    const searchValue = useRef<string>("");
    const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

    useEffect(() => {
            loadPermissions();
    }, [permissionsLoaded]);

    const LanguageInterfaceCreate = hasPermission("LANGUAGE_INTERFACE", "CREATE");
    const LanguageInterfaceEdit = hasPermission("LANGUAGE_INTERFACE", "UPDATE");
    const LanguageInterfaceDelete = hasPermission("LANGUAGE_INTERFACE", "DELETE");

    useEffect(() => {
        let userRoleId = parseInt(storage.getUserRoleId() ?? "0");
        if (![1, 4].includes(userRoleId)) {
            navigate("/");
        }
        else {
            getLanguageInterfacesList("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const showDrawer = (isEditState: boolean) => {
        isAddLanguageInterface.current = !isEditState;
        if (isAddLanguageInterface.current) {
            setEditingLanguageInterface(null);
            form.resetFields();
        }
        setOpen(true);
        eventEmitter.emit("clickableChange", true);
    };

    const onClose = () => {
        form.resetFields();
        setOpen(false);
        eventEmitter.emit("clickableChange", false);
    };


    const getLanguageInterfacesList = async (searchFilter: string) => {
        setIsLoading(true);
        getLanguageInterfaceMutation.mutate({
            searchFilter: searchFilter
        });
    };

    const getLanguageInterfaceMutation = useFetchLanguageInterface({
        onSuccess: (response: any) => {
            if (isNotNullUndefined(response)) {
                setLanguageinterfaces(response.data);
                setIsLoading(false);
            }
        }, onError: (error) => {
            message.error(error.message);
            setIsLoading(false);
        }
    });

    const addLanguageInterface = useAddLanguageInterface({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                getLanguageInterfacesList("");
                let succesMessage = "Language Interface has been added successfully!";
                setIsLoading(false);
                message.success(succesMessage);
            } else {
                setIsLoading(false);
                message.error(response.statusMessage);
            }
            onClose();
            form.resetFields();
        },
        onError: (error) => {
            setIsLoading(false);
            message.error(error.statusMessage);
        }
    });

    const updateLanguageInterface = useUpdateLanguageInterface({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                getLanguageInterfacesList("");
                let succesMessage = "Language Interface has been updated succesfullt!";
                setIsLoading(false);
                message.success(succesMessage);
            }
            else {
                setIsLoading(false);
                message.error(response.statusMessage);
            }
            onClose();
        },
        onError: (error) => {
            setIsLoading(false);
            message.error(error.statusMessage);
        }
    });

    const deleteLanguageInterface = useDeleteLanguageInterface({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                getLanguageInterfacesList("");
                let successMessage = "Language Interface has been deleted Succesfully!";
                setIsLoading(false);
                message.success(successMessage);
            } else {
                setIsLoading(false);
                message.error(response.statusMessage);
            }
            onClose();
        },
        onError: (error: any) => {
            setIsLoading(false);
            message.error(error.statusMessage);
        }
    });

    const handleSave = async () => {
        try {
            if (modalType === 'delete') {
                deleteLanguageInterface.mutate(languageinterfaceId);
            }
            else if (modalType === 'update' && editingLanguageInterface) {
                setIsLoading(true);
                const editedData = { ...editingLanguageInterface, ...formUpdateValues };
                updateLanguageInterface.mutate({ interfaceLanguageID: editedData.interfaceLanguageID, interfaceLanguageCode: editedData.interfaceLanguageCode, interfaceLanguageName: editedData.interfaceLanguageName, interfaceLanguageIsActive: editedData.interfaceLanguageIsActive });
            }
            else if (modalType === 'add') {
                setIsLoading(true);
                addLanguageInterface.mutate({ interfaceLanguageCode: formUpdateValues.interfaceLanguageCode, interfaceLanguageName: formUpdateValues.interfaceLanguageName, interfaceLanguageIsActive: formUpdateValues.interfaceLanguageIsActive });
            }
            setIsConfirmModalVisible(false);
        } catch (error) {
            message.error('Failed to save');
        }
    };

    const onFinish = (values: any) => {
        setModalType(((isAddLanguageInterface.current) ? 'add' : 'update'));
        if (modalType === 'update') {
            formUpdatedValues.current =
            {
                interfaceLanguageID: values.interfaceLanguageID,
                interfaceLanguageCode: values.interfaceLanguageCode,
                interfaceLanguageName: values.interfaceLanguageName,
                interfaceLanguageIsActive: values.interfaceLanguageIsActive
            }
            if (compareObjects(formInitialValues.current, formUpdatedValues.current)) {
                onClose();
                return;
            }
        }
        setIsConfirmModalVisible(true);
        setFormUpdateValues(values);
    }


    const getModalTitle = () => {
        let message = "";
        if (modalType === 'delete') {
            message = "Confirm Deletion!";
        } else if (modalType === 'update') {
            message = "Confirm Update!";
        }
        else if (modalType === 'add') {
            message = "Confirm Add!";
        }
        return message;
    }

    const handleCancel = () => {
        setIsLoading(false);
        setIsConfirmModalVisible(false);
    };

    const onEditLanguageInterface = (record: any) => {
        setEditingLanguageInterface(record);
        form.setFieldsValue({
            interfaceLanguageID: record.interfaceLanguageID,
            interfaceLanguageCode: record.interfaceLanguageCode,
            interfaceLanguageName: record.interfaceLanguageName,
            interfaceLanguageIsActive: record.interfaceLanguageIsActive
        });
        formInitialValues.current = {
            interfaceLanguageID: record.interfaceLanguageID,
            interfaceLanguageCode: record.interfaceLanguageCode,
            interfaceLanguageName: record.interfaceLanguageName,
            interfaceLanguageIsActive: record.interfaceLanguageIsActive
        }
        showDrawer(true);
    }

    const onDeleteLanguageInterface = (record: any) => {
        setModalType('delete');
        setIsConfirmModalVisible(true);
        setFormUpdateValues({ interfaceLanguageName: record.interfaceLanguageName });
        setLanguageinterfaceId(record.interfaceLanguageID);
    };

    const getModelContent = () => {
        let modalContent = <></>;
        if (modalType === 'delete') {
            modalContent = (
                <p>Are you sure you want to delete <b>{formUpdateValues.interfaceLanguageName}</b> Language Interface's Delete request? Once deleted, this Language Interface will not be recovered. Do you wish to proceed?</p>
            );
        } else if (modalType === 'update') {
            modalContent = (
                <p>Are you sure you want to update <b>{formUpdateValues.interfaceLanguageName}</b> Language Interface's Update request? Once updated, this Language Interface can be edited and their details will be updated accordingly. Do you wish to proceed?</p>
            );
        }
        else if (modalType === 'add') {
            modalContent = (
                <p>Are you sure you want to add <b>{formUpdateValues.interfaceLanguageName}</b> Language Interface's Add request? Once added, this Language Interface can be edited and their details will be updated accordingly. Do you wish to proceed?</p>
            );
        }
        return modalContent;
    }

    const onSearch = (value: any) => {
        getLanguageInterfacesList(value);
        searchValue.current = value;
    };

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-2xl font-semibold text-black">Language Interfaces</h2>
                <div className="flex justify-between gap-5">
                    <div className="w-80 max-w-80">
                        <Search
                            placeholder="Search Language Interfaace"
                            size="large"
                            enterButton
                            className="search-field"
                            onSearch={onSearch}
                        />
                    </div>
                    {LanguageInterfaceCreate && (
                    <Button className="h-10 bg-black text-white hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out" onClick={() => showDrawer(false)} icon={<PlusOutlined />}>
                        Add Language Interface
                    </Button>)}
                </div>
            </div>
            <Drawer
                title={((isAddLanguageInterface.current) ? 'Add New' : 'Edit') + ' Language Interface'}
                width={370}
                onClose={onClose}
                mask={false}
                open={open}>
                <Form layout="vertical" form={form} onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="interfaceLanguageID"
                                label="Interface Language ID"
                                style={{ display: 'none' }}
                            >
                                <Input type="hidden" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="interfaceLanguageCode" label="Code"
                                rules={[{ required: true, message: 'Please enter Language interface Code' }]}>
                                <Input size="large" placeholder="Please enter Language interface Code" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="interfaceLanguageName" label="Name"
                                rules={[{ required: true, message: 'Please enter Language interface name' }]}>
                                <Input size="large" placeholder="Please enter Language interface name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="interfaceLanguageIsActive" className="mb-0" label="Status:" valuePropName="checked">
                                <Checkbox className="check-box">Active</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space className="w-full justify-end">
                        <Button onClick={onClose} className="hover:!text-black hover:!border-black">Cancel</Button>
                        <Button htmlType="submit" className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black">{(isAddLanguageInterface.current) ? 'Add' : 'Update'}</Button>
                    </Space>
                </Form>
            </Drawer>
            <Table dataSource={languageinterfaces} className="userlist-table" bordered
                locale={{
                    emptyText: isLoading ? <Spin /> : (
                        <div className="ant-empty">
                            <div className="ant-empty-image">
                                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                        <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                        <g fillRule="nonzero" stroke="#d9d9d9">
                                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                            <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                        </g>
                                    </g>
                                </svg>
                            </div>
                            <div className="ant-empty-description">No data</div>
                        </div>
                    )
                }}>
                <Column title="Code" dataIndex="interfaceLanguageCode" key="interfaceLanguageCode" align="center" />
                <Column title="Name" dataIndex="interfaceLanguageName" key="interfaceLanguageName" align="center" />
                <Column
                    align="center"
                    title="Status"
                    dataIndex="interfaceLanguageIsActive"
                    key="interfaceLanguageIsActive"
                    render={(status) => (
                        <>
                            <Tag key={status} color={status === true ? "green" : "red"} className="m-0 pl-1 pr-1">
                                {status == true ? "ACTIVE" : "DEACTIVATED"}
                            </Tag>
                        </>
                    )}
                />
                <Column
                    align="center"
                    title="Action"
                    key="action"
                    render={(_: any, record: any) => (
                        <Space size="middle">
                            {LanguageInterfaceEdit && <Button onClick={() => onEditLanguageInterface(record)} icon={<EditOutlined />} className="hover:!border-black hover:!text-black"></Button>}
                            {LanguageInterfaceDelete && record.interfaceLanguageIsActive !== false && (<Button onClick={() => onDeleteLanguageInterface(record)} icon={<DeleteOutlined />} className="hover:!border-black hover:!text-black"></Button>)}
                        </Space>
                    )}
                />

            </Table>

            <Modal
                title={<span className="custom-modal-title">{getModalTitle()}</span>}
                open={isConfirmModalVisible}
                onOk={handleSave}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-gray-500 hover:text-black" />}
                footer={[
                    <Button key="back" onClick={handleCancel} className="hover:!text-black hover:!border-black">
                        Cancel
                    </Button>,
                    <Button key="submit" onClick={handleSave} className="bg-black text-white hover:bg-transparent hover:!text-black hover:!border-black">
                        Confirm
                    </Button>,
                ]}
            >
                {getModelContent()}
            </Modal>
        </div>
    );
}
export default Languageinterface;